import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';
import type { ListRenderItemInfo } from 'react-native';

import * as api from '@gitbook/api-client';
import { getDefaultSiteTypeForOrg } from '@gitbook/app-pricing';
import { PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIllustration,
    Heading,
    Link,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    ListSkeleton,
    Pagination,
    ProgressiveImage,
    TooltipIndicator,
    toast,
} from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';

import { useNavigation } from '../../../navigation';
import {
    apiClient,
    apiQuery,
    apiResource,
    type SiteUpgradeAction,
    useSuspensePaginatedAPIQuery,
} from '../../../state';
import { useOrganizationSites } from '../../Organization';
import {
    getAddSpaceToSitePaletteFactory,
    getSitePaletteFactory,
    getSiteVisibilityIllustration,
} from '../../Site';
import { SiteContentIcon } from '../../Site/SiteIcon';
import { CopyableURL } from '../../URL/CopyableURL';
import type { ShareContentModalProps } from '../types';

const logger = Logger('PublishSpace');

/**
 * Tab to publish a space to a docs site.
 */
export function PublishSpace(props: ShareContentModalProps): React.ReactElement {
    const { content, organization } = props;
    const { navigate } = useNavigation();

    if ('collection' in content) {
        // should never end up here as the visibility check should prevent this
        throw Error('Publishing can only be used with a space');
    }

    const space = content.space;
    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: space.organization,
        })
    );

    const sites = useOrganizationSites(organizationContent, {
        spaceId: space.id,
    });
    const primarySpaceSite = sites[0]?.site;

    const renderConnectedSiteItem = React.useCallback(
        ({ item }: ListRenderItemInfo<api.OrganizationAllSite>) => {
            return <SpaceSiteListItem site={item.site} organizationId={organization.id} />;
        },
        [organization.id]
    );

    if (!sites.length || !primarySpaceSite) {
        return <SitesEmptyState space={space} organization={organization} />;
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Space sites list */}
            <div className="flex flex-col gap-4 divide-y">
                <div className="flex flex-col gap-2">
                    <span className="text-muted text-ui-small">Connected to</span>
                    <List items={sites} renderItem={renderConnectedSiteItem} />
                </div>
                {primarySpaceSite.visibility === api.SiteVisibility.ShareLink ? (
                    <div className="flex flex-col gap-4 pt-6">
                        <div className="flex justify-between">
                            <div className="text-muted text-ui-small">Share links</div>
                            <Link
                                onPress={() => {
                                    navigate({
                                        screen: {
                                            type: 'site',
                                            site: primarySpaceSite.id,
                                        },
                                    });
                                }}
                                label="Manage"
                            />
                        </div>
                        <React.Suspense fallback={<ListSkeleton />}>
                            <ShareLinkList
                                site={primarySpaceSite}
                                organizationId={space.organization}
                            />
                        </React.Suspense>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Empty state for spaces not connected to any docs sites.
 */
function SitesEmptyState(props: { space: api.Space; organization: api.Organization }) {
    const { space, organization } = props;
    const { navigate } = useNavigation();
    const canCreateSites = organization.permissions.createContent;

    const onCreateSite = async () => {
        await toast.catch(async () => {
            const { data: newSite } = await apiClient.sites.createSite(space.organization, {
                title: space.title || undefined,
                spaces: [space.id],
                type: getDefaultSiteTypeForOrg(organization),
            });

            navigate({
                screen: { type: 'site', site: newSite.id },
            });

            toast.success('New docs site created', {
                description: `Your space ${space.title} is now connected to the newly created site ${newSite.title} `,
            });
        }, 'An error occurred while creating new docs site');
    };

    return (
        <EmptyState>
            <EmptyStateIllustration>
                <ProgressiveImage
                    width={48}
                    height={48}
                    src={getSiteVisibilityIllustration(api.SiteVisibility.Public)}
                />
            </EmptyStateIllustration>
            {canCreateSites ? (
                <>
                    <Heading>Publish to a docs site</Heading>
                    <Description>
                        Share your content on the web by publishing it as a new docs site or adding
                        it to an existing one.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            kind="secondary"
                            label="Create new site"
                            leadingIcon={Icon.Plus}
                            onPress={onCreateSite}
                        />
                        <PaletteButton
                            kind="primary"
                            label="Add to existing site..."
                            leadingIcon={Icon.Link}
                            palette={getAddSpaceToSitePaletteFactory({ space, navigate })}
                        />
                    </EmptyStateActions>
                </>
            ) : (
                <Heading>This space is not linked to any docs sites</Heading>
            )}
        </EmptyState>
    );
}

/**
 * List item to view/manage a site the space is connected to.
 *
 * Displays the site's title, publish status icon, and actions.
 */
function SpaceSiteListItem(props: { site: api.Site; organizationId: string }): React.ReactElement {
    const { site, organizationId } = props;
    const { navigate } = useNavigation();

    return (
        <ListItem>
            <ListItemIndicator>
                <div className="flex items-center justify-center rounded-full bg-muted text-muted">
                    <SiteContentIcon site={site} size={20} />
                </div>
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{site.title}</ListItemDescription>
                {site.urls.published ? (
                    <ListItemDetail>
                        <Link href={site.urls.published} target="_blank">
                            {site.urls.published}
                        </Link>
                    </ListItemDetail>
                ) : null}
            </ListItemBody>
            <ListItemControls when="always">
                <PaletteButton
                    kind="blank"
                    label="Site actions"
                    palette={getSitePaletteFactory({
                        navigate,
                        site,
                        organizationId,
                    })}
                    leadingIcon={Icon.MoreVertical}
                    iconOnly
                />
            </ListItemControls>
        </ListItem>
    );
}

export const DEFAULT_SHARE_LINK_NAME = 'Untitled';

function ShareLinkList(props: {
    site: api.Site;
    organizationId: string;
    upgrade?: SiteUpgradeAction;
}) {
    const { site, organizationId } = props;
    const {
        data: shareLinks,
        next,
        previous,
    } = useSuspensePaginatedAPIQuery(
        apiResource('listSiteShareLinks', { organizationId, siteId: site.id })
    );

    const renderItem = React.useCallback(({ item }: ListRenderItemInfo<api.ShareLink>) => {
        if (!item.urls?.published) {
            logger.error('Share link has no published URL', item);
            return <></>;
        }

        return (
            <ListItem className="mb-2 bg-secondary">
                <ListItemBody>
                    <ListItemDescription className="text-light">
                        <div
                            className={clsx(
                                'flex items-center gap-2',
                                item.active === false && 'text-light'
                            )}
                        >
                            {item.name || DEFAULT_SHARE_LINK_NAME}

                            {item.active === false ? (
                                <TooltipIndicator label="Inactive" className="size-3" />
                            ) : null}
                        </div>
                    </ListItemDescription>
                    <ListItemDetail>
                        <CopyableURL url={item.urls.published} when="always" />
                    </ListItemDetail>
                </ListItemBody>
            </ListItem>
        );
    }, []);

    return (
        <>
            <List items={shareLinks?.items} renderItem={renderItem} />
            <Pagination next={next} previous={previous} />
        </>
    );
}
